import { ModuleManifest } from '../services/ModuleScanner';

export class HotPluginManager {
  private cache: Map<string, ModuleManifest> = new Map();
  private listeners: Set<() => void> = new Set();
  private ws: WebSocket | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.connectWebSocket();
    }
  }

  private connectWebSocket() {
    // Suppress hot-reload socket storm
    if (typeof window !== "undefined") {
      const isChatProfile = document.documentElement.getAttribute("data-mesh-profile") === "msng3r-chat";
      if (isChatProfile || (this as any)._aborted) return;
    }
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    this.ws = new WebSocket(`${wsProtocol}//${window.location.host}`);
    
    this.ws.onerror = () => {
      (this as any)._aborted = true;
      if (this.ws) { try { this.ws.close(); } catch {} }
    };

    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'modules:catalog-updated') {
          this.fetchCatalog();
        }
        if (data.type === 'modules:status-changed') {
          this.updateModuleStatus(data.moduleId, data.enabled);
          if (typeof window !== 'undefined') {
            try {
              const current = JSON.parse(localStorage.getItem('enabled_plugins') || '{}');
              current[data.moduleId] = data.enabled;
              localStorage.setItem('enabled_plugins', JSON.stringify(current));
              window.dispatchEvent(new CustomEvent('enabled-plugins-changed', { detail: current }));
            } catch {}
          }
        }
      } catch {}
    };

    this.ws.onclose = () => {
      if ((this as any)._aborted) return;
      setTimeout(() => this.connectWebSocket(), 3000);
    };
  }

  async fetchCatalog(): Promise<void> {
    try {
      const res = await fetch('/api/modules/catalog');
      const { core, decoupled } = await res.json();
      this.cache.clear();
      [...(core || []), ...(decoupled || [])].forEach((mod: ModuleManifest) => {
        this.cache.set(mod.id, mod);
      });
      this.notifyListeners();
    } catch {}
  }

  private updateModuleStatus(moduleId: string, enabled: boolean) {
    const mod = this.cache.get(moduleId);
    if (mod) {
      mod.status = enabled ? 'enabled' : 'disabled';
      this.notifyListeners();
    }
  }

  subscribe(onUpdate: () => void): () => void {
    this.listeners.add(onUpdate);
    return () => this.listeners.delete(onUpdate);
  }

  private notifyListeners() {
    this.listeners.forEach(fn => fn());
  }

  getModules(): ModuleManifest[] {
    return Array.from(this.cache.values());
  }
}

export const hotPluginManager = new HotPluginManager();
